/* eslint-disable func-names */
/* eslint-disable no-multi-assign */
/* eslint-disable prefer-rest-params */
import React, { useEffect, useRef, useState } from 'react';
import { Provider, useSelector } from 'react-redux';
import Head from 'next/head';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import Intercom from '@intercom/messenger-js-sdk';
import { AppWrapper } from '../context/AppContext';
import AppThemeProvider from '../theme/AppThemeProvider';
import store from '@/store/store';
import translations from '@/translations/_app.trans.json';
import Trans from '@/components/shared/Trans';
import DictionaryNamePage from '@/components/shared/DictionaryNamePage';
import '../styles/globals.scss';
import ErrorBoundary from '@/components/ErrorBudanry';
import IntercomService from '@/services/ItercomService';
import PageSkeleton from '@/components/SettingsReactLoader/PageSkeleton/PageSkeleton';
import { initGtm, pushToDataLayer } from '@/utils/gtm';

const SettingsReactLoader = dynamic(() =>
  import('@/components/SettingsReactLoader/SettingsReactLoader')
);
const CustomModalsWrapped = dynamic(
  () => import('@/components/CustomModalsWrapped/CustomModalsWrapped'),
  {
    loading: () => <PageSkeleton />,
  }
);
const Layout = dynamic(() => import('@/components/Layout/Layout'), {
  ssr: false,
  loading: () => <PageSkeleton />,
});

function GtmUserContext() {
  const userData = useSelector((state) => state.UserSlice.data);
  const lastPayloadRef = useRef(null);
  const hasInitializedGtmRef = useRef(false);

  useEffect(() => {
    // Push user context to dataLayer before initializing GTM.
    if (typeof window === 'undefined') return;
    const gtmId = process.env.NEXT_PUBLIC_GTM_ID || 'GTM-5WVHQNX';

    // If user becomes unavailable (logout), clear context.
    if (!userData) {
      if (lastPayloadRef.current != null) {
        lastPayloadRef.current = null;
        pushToDataLayer({
          event: 'user_context_cleared',
          user_id: null,
          country: null,
          user_type: null,
          is_staff: null,
        });
      }
      return;
    }

    const { id, country_iso_code, user_type, is_staff } = userData || {};
    if (id == null || !country_iso_code || !user_type || is_staff == null)
      return;

    const payload = {
      event: 'user_context',
      user_id: String(id),
      country: country_iso_code,
      user_type,
      is_staff,
    };

    const payloadKey = JSON.stringify(payload);
    if (lastPayloadRef.current === payloadKey) return;
    lastPayloadRef.current = payloadKey;

    pushToDataLayer(payload);

    // Ensure user context is queued before the initial `event: 'gtm.js'`.
    if (!hasInitializedGtmRef.current) {
      initGtm({ gtmId });
      hasInitializedGtmRef.current = true;
    }
  }, [
    userData?.id,
    userData?.country_iso_code,
    userData?.user_type,
    userData?.is_staff,
  ]);

  return null;
}

function IntercomInitializer() {
  useEffect(() => {
    IntercomService.getIntercomData()
      .then((res) => {
        const intercomData = res?.intercom_data;
        if (!intercomData) return;
        intercomData.hide_default_launcher = false;
        Intercom(intercomData);
      })
      .catch((error) => console.log(error));
    return () => {
      Intercom({ hide_default_launcher: true });
    };
  }, []);

  return null;
}

function AppHead({ pathname }) {
  const { faviconPath, crmName, theme } = useSelector(
    (state) => state.UserSlice.themeConfig
  );

  const isThemeReady = Boolean(crmName && faviconPath);
  const pageName =
    DictionaryNamePage[pathname] &&
    Trans(translations, DictionaryNamePage[pathname]);
  const cacheBuster = new Date().toISOString();

  if (!isThemeReady) {
    return (
      <Head>
        <title>{`${Trans(translations, 'Loading')}...`}</title>
        <meta
          name="description"
          content={`${Trans(translations, 'Loading')}`}
        />
        <link rel="icon" type="image/x-icon" href="/favicon-skeleton.png" />
      </Head>
    );
  }

  return (
    <Head>
      <title>{`${pageName || ''} | ${crmName}`}</title>
      <meta name="description" content={crmName} />
      {theme === 'univen' ? (
        <>
          <link
            rel="icon"
            type="image/png"
            sizes="16x16"
            href={`/univen-favicon-16x16.png?v=${cacheBuster}`}
          />
          <link
            rel="icon"
            type="image/png"
            sizes="32x32"
            href={`/univen-favicon-32x32.png?v=${cacheBuster}`}
          />
          <link
            rel="icon"
            type="image/png"
            sizes="48x48"
            href={`/univen-favicon-48x48.png?v=${cacheBuster}`}
          />
        </>
      ) : (
        <link
          rel="icon"
          type="image/x-icon"
          href={`${faviconPath}?v=${cacheBuster}`}
        />
      )}
    </Head>
  );
}

const PublicRoute = React.memo(({ Component, pageProps }) => (
  <ErrorBoundary>
    <Component {...pageProps} />
  </ErrorBoundary>
));

function ProtectedRoute({
  Component,
  pageProps,
  isEnabledToken,
  setEnabledToken,
}) {
  const isLayoutRemoved = Component.layoutRemoved;

  return isEnabledToken ? (
    <ErrorBoundary>
      <CustomModalsWrapped>
        {isLayoutRemoved ? (
          <Component {...pageProps} />
        ) : (
          <Layout>
            <IntercomInitializer />
            <Component {...pageProps} />
          </Layout>
        )}
      </CustomModalsWrapped>
    </ErrorBoundary>
  ) : (
    <SettingsReactLoader setEnabledToken={setEnabledToken} />
  );
}

function MyApp({ Component, pageProps }) {
  const [isEnabledToken, setEnabledToken] = useState(false);

  const router = useRouter();
  const isPublicRoute = Component.public;

  useEffect(() => {
    // Preload heavy dynamic chunks while the loader is visible
    if (!isEnabledToken) {
      if (typeof CustomModalsWrapped?.preload === 'function') {
        CustomModalsWrapped.preload();
      }
      if (typeof Layout?.preload === 'function') {
        Layout.preload();
      }
    }
  }, [isEnabledToken]);

  return (
    <Provider store={store}>
      <AppThemeProvider>
        <AppWrapper>
          <GtmUserContext />
          <AppHead pathname={router.pathname} />
          {isPublicRoute ? (
            <PublicRoute Component={Component} pageProps={pageProps} />
          ) : (
            <ProtectedRoute
              Component={Component}
              pageProps={pageProps}
              router={router}
              isEnabledToken={isEnabledToken}
              setEnabledToken={setEnabledToken}
            />
          )}
        </AppWrapper>
      </AppThemeProvider>
    </Provider>
  );
}

export default MyApp;
